import { deviceLinkLocalhostRoot } from "./Devices";
import { HashResult } from "./Models/HashResult";
import { RecordingFile } from "./Models/Cough/RecordingFileEnum";
import { DeviceNotification } from "./Models/DeviceNotification";

export interface VitaloJak2Configuration {
    sampleRate: number;
    button1Enabled: boolean;
    button2Enabled: boolean;
    button3Enabled: boolean;
    button4Enabled: boolean;
    muteEnabled: boolean;
    memoryTarget: MemoryTarget;
    visitSelectionEnabled: boolean;
    hardwareTestEnabled: boolean;
}

export interface VitaloJak2DeviceInformation {
    deviceId: string;
    softwareId: string;
    hardwareRevision: string;
    softwareRevision: string;
    deviceSerial: string;
}

export interface VitaloJak2Study {
    studyId: string;
}

export interface VitaloJak2Site {
    siteId: string;
}

export interface VitaloJak2Subject {
    subjectId: string;
}

export interface VitaloJak2DataCheck {
    hasRecordings: boolean;
}

export interface VitaloJak2Session {
    sessionId: string;
    externalCode: string;
    recordingLength: number;
}

export interface VitaloJak2Recording {
    id: string;
    externalCode: string;
    recordingLength: number;
    microphonePath: string;
    sensorPath: string;
    logPath: string;
}

export interface VitaloJak2DeviceDateTime {
    year: number;
    month: number;
    day: number;
    hours: number;
    minutes: number;
    seconds: number;
}

export enum LogContext {
    device = 0,
    clinician = 1,
    device_link = 2,
}

export enum VitaloJak2LogEventCode {
    powerOn = 1,
    powerOff = 2,
    recordingStarted = 3,
    recordingEnded = 4,

    button1Pressed = 11,
    button2Pressed = 12,
    button3Pressed = 13,
    button4Pressed = 14,
    muteOn = 15,
    muteOff = 16,

    serialNumberConfigured = 20,
    subjectIdConfigured = 21,
    studyNumberConfigured = 22,
    siteIdConfigured = 23,
    deviceSettingsConfigured = 24,
    deviceRealTimeClockSet = 25,
    visitAdded = 26,
    wipeAllVisits = 27,

    memoryError = 31,
    microphoneShortError = 32,
    microphoneDetachedError = 33,
    sensorShortError = 34,
    sensorDetachedError = 35,
    backCoverRemovedError = 36,
    insertSDCardError = 37,
    removeSDCardError = 38,
    realTimeClockError = 39,
    genericError = 40,

    backCoverReplaced = 50
}

export interface VitaloJak2LogFile {
    subjectNumber: string;
    microphoneIdentifier: string;
    sensorIdentifier: string;
    records: Array<VitaloJak2LogFileRecord>;
}

export interface VitaloJak2LogFileRecord {
    dateTime: string;
    context: string;
    eventCode: VitaloJak2LogEventCode;
    data1: string;
    data2: string;
}

export enum MemoryTarget {
    Internal = 0,
    External = 1,
}

export const ApiCheckData = (deviceId: string): Promise<VitaloJak2DataCheck> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/CheckData`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
    })
        .then(response => response.json())
}

export const ApiDeviceInformation = (deviceId: string): Promise<VitaloJak2DeviceInformation> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetDeviceInformation`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
    })
        .then(response => response.json())
}

export const ApiFormat = (deviceId: string): Promise<boolean> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/Format`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
    })
        .then(response => response.ok)
}

export const ApiGetConfiguration = (deviceId: string): Promise<VitaloJak2Configuration> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetConfiguration`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
    })
        .then(response => {
            if (!response.ok) return null;
            if (response.status === 204) return null;

            return response.json();
        })
}

export const ApiSetConfiguration = (deviceId: string, configuration: VitaloJak2Configuration): Promise<boolean> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/SetConfiguration`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
        body: JSON.stringify(configuration),
    })
        .then(response => response.ok)
}

export const ApiGetStudy = (deviceId: string): Promise<VitaloJak2Study> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetStudy`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
    })
        .then(response => response.json())
}

export const ApiSetStudy = (deviceId: string, study: VitaloJak2Study): Promise<boolean> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/SetStudy`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
        body: JSON.stringify(study),
    })
        .then(response => response.ok)
}

export const ApiGetSite = (deviceId: string): Promise<VitaloJak2Site> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetSite`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
    })
        .then(response => response.json())
}

export const ApiSetSite = (deviceId: string, site: VitaloJak2Site): Promise<boolean> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/SetSite`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
        body: JSON.stringify(site),
    })
        .then(response => response.ok)
}

export const ApiGetSubject = (deviceId: string): Promise<VitaloJak2Subject> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetSubject`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
    })
        .then(response => response.json())
}

export const ApiSetSubject = (deviceId: string, subject: VitaloJak2Subject): Promise<boolean> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/SetSubject`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
        body: JSON.stringify(subject),
    })
        .then(response => response.ok)
}

export const ApiAddSession = (deviceId: string, session: VitaloJak2Session): Promise<boolean> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/AddSession`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
        body: JSON.stringify(session),
    })
        .then(response => response.ok)
}

export const ApiDeleteSessions = (deviceId: string): Promise<boolean> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/DeleteSessions`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
    })
        .then(response => response.ok)
}

export const ApiSetDateTime = (deviceId: string, dateTime: VitaloJak2DeviceDateTime): Promise<boolean> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/SetDateTime`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
        body: JSON.stringify(dateTime),
    })
        .then(response => response.ok)
}

export const ApiGetRecordings = (deviceId: string): Promise<Array<VitaloJak2Recording>> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetRecordings`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
    })
        .then(response => response.json())
}

export const ApiGetLogFile = (deviceId: string, recordingId: string): Promise<VitaloJak2LogFile> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetLogFile`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
        body: JSON.stringify({ id: recordingId }),
    })
        .then(async response => {  
            if (response.ok) {
                return await response.json();
            } else {
                const error = await response.json();
                throw new Error(error.title);
            }
        })
}

export const ApiGetLogFileStream = (deviceId: string, recordingId: string): Promise<Response> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetLogFileStream`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
        body: JSON.stringify({ id: recordingId }),
    })
        .then(response => response);
}

export const ApiGetAuditFileStream = (deviceId: string): Promise<Response> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetAuditTrailStream`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        }
    })
        .then(response => response);
}

export const ApiGetMicrophoneRecording = (deviceId: string, bytesFrom: number, bytesTo: number, recordingId: string): Promise<Response> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetMicrophoneRecording`, {
        method: 'POST',
        headers: {
            'Range': `bytes=${bytesFrom}-${bytesTo}`,
            'content-type': 'application/json',
        },
        body: JSON.stringify({ id: recordingId }),
    })
        .then(response => response);
}

export const ApiGetSensorRecording = (deviceId: string, bytesFrom: number, bytesTo: number, recordingId: string): Promise<Response> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetSensorRecording`, {
        method: 'POST',
        headers: {
            'Range': `bytes=${bytesFrom}-${bytesTo}`,
            'content-type': 'application/json',
        },
        body: JSON.stringify({ id: recordingId }),
    })
        .then(response => response);
}

export const ApiGetMicrophoneRecordingHash = (deviceId: string, recordingId: string): Promise<HashResult> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetMicrophoneRecordingHash`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
        body: JSON.stringify({ id: recordingId }),
    })
        .then(response => response.json());
}

export const ApiGetSensorRecordingHash = (deviceId: string, recordingId: string): Promise<HashResult> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetSensorRecordingHash`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
        body: JSON.stringify({ id: recordingId }),
    })
        .then(response => response.json());
}

export const ApiGetAuditTrailHash = (deviceId: string, recordingId: string): Promise<HashResult> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetAuditTrailHash`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
        body: JSON.stringify({ id: recordingId }),
    })
        .then(response => response.json());
}

export const ApiGetLogHash = (deviceId: string, recordingId: string): Promise<HashResult> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetLogHash`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
        body: JSON.stringify({ id: recordingId }),
    })
        .then(response => response.json());
}

export const ApiGetDeviceNotifications = (deviceId: string): Promise<Array<DeviceNotification>> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/GetDeviceNotifications`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
    })
        .then(async response => {
            if (response.ok) {
                return await response.json();
            } else {
                const error = await response.json();
                throw new Error(error.title);
            }
        });
}

export const ApiReset = (deviceId: string): Promise<boolean> => {
    return fetch(`${deviceLinkLocalhostRoot}/devices/${deviceId}/Reset`, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
        },
    })
        .then(response => response.ok)
}
